import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Vote } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { populationsApi } from '@/lib/api'
import type { AudienceSummary } from '@/lib/api'
import CinematicAudienceCard from './CinematicAudienceCard'

interface FilterEmptyStateProps {
  selectedCategoryPath?: string
  selectedCountryCodes: Array<string>
  audienceTypeFilter: 'B2B' | 'B2C' | null
  allAudiences: Array<AudienceSummary>
  onAudienceClick: (id: string) => void
  formatRelativeTime: (date: string) => string
  onBookmarkToggle?: (id: string, nextBookmarked: boolean) => Promise<void>
}

function buildVoteKey(
  categoryPath?: string,
  countryCodes?: Array<string>,
  audienceType?: string | null,
): string {
  const parts = [
    categoryPath ?? '',
    (countryCodes ?? []).slice().sort().join('+'),
    audienceType ?? '',
  ]
  return `coverage-vote:${parts.join(':')}`
}

function hasVoted(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function markVoted(key: string): void {
  try {
    localStorage.setItem(key, '1')
  } catch {
    // localStorage unavailable
  }
}

function useSuggestions(
  allAudiences: Array<AudienceSummary>,
  selectedCategoryPath?: string,
  audienceTypeFilter?: 'B2B' | 'B2C' | null,
): Array<AudienceSummary> {
  return useMemo(() => {
    const ready = allAudiences.filter((a) => a.status === 'ready')
    if (ready.length === 0) return []

    if (selectedCategoryPath) {
      const parentSegments = selectedCategoryPath.split(' > ')
      for (let depth = parentSegments.length - 1; depth >= 1; depth--) {
        const parentPrefix = parentSegments.slice(0, depth).join(' > ')
        const matches = ready.filter((a) =>
          a.contentLabels?.some((l) => l.fullPath.startsWith(parentPrefix)),
        )
        if (matches.length > 0) return matches.slice(0, 4)
      }
    }

    if (audienceTypeFilter) {
      const typeMatches = ready.filter(
        (a) => a.audienceType === audienceTypeFilter,
      )
      if (typeMatches.length > 0) return typeMatches.slice(0, 4)
    }

    return ready
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .slice(0, 4)
  }, [allAudiences, selectedCategoryPath, audienceTypeFilter])
}

export function FilterEmptyState({
  selectedCategoryPath,
  selectedCountryCodes,
  audienceTypeFilter,
  allAudiences,
  onAudienceClick,
  formatRelativeTime,
  onBookmarkToggle,
}: FilterEmptyStateProps) {
  const { t } = useTranslation('app')
  const voteKey = buildVoteKey(
    selectedCategoryPath,
    selectedCountryCodes,
    audienceTypeFilter,
  )
  const [voted, setVoted] = useState(() => hasVoted(voteKey))
  const [animating, setAnimating] = useState(false)

  const suggestions = useSuggestions(
    allAudiences,
    selectedCategoryPath,
    audienceTypeFilter,
  )

  const handleVote = useCallback(async () => {
    if (voted || animating) return

    setAnimating(true)

    try {
      await populationsApi.requestCoverage({
        taxonomyPath: selectedCategoryPath,
        countries: selectedCountryCodes.length > 0 ? selectedCountryCodes : undefined,
        audienceType: audienceTypeFilter ?? undefined,
      })
    } catch {
      // Fire-and-forget — don't block the animation
    }

    markVoted(voteKey)
    setTimeout(() => {
      setVoted(true)
      setAnimating(false)
    }, 600)
  }, [
    voted,
    animating,
    voteKey,
    selectedCategoryPath,
    selectedCountryCodes,
    audienceTypeFilter,
  ])

  return (
    <div className="col-span-full space-y-8" data-testid="filter-empty-state">
      {/* Main card */}
      <div
        className="rounded-lg border border-dashed border-border/60 flex flex-col items-center justify-center text-center px-5 py-10 md:py-14 gap-4"
        data-testid="filter-empty-state-card"
      >
        <div className="flex flex-col items-center gap-2 max-w-md">
          <Heading
            level={3}
            className="text-lg font-medium font-sans text-foreground"
          >
            {t('audienceActions.noFilterMatch')}
          </Heading>
          <Text size="base" color="muted">
            {t('audienceActions.beFirst')}
          </Text>
        </div>

        <Button
          data-testid="coverage-vote-button"
          variant={voted ? 'outline' : 'cta'}
          disabled={voted}
          onClick={handleVote}
          className={cn(
            'rounded-lg h-10 px-5 text-base font-medium transition-all duration-300',
            animating && 'scale-110',
            voted && 'border-green-500/50 text-green-600 dark:text-green-400',
          )}
        >
          {voted ? (
            <>
              <Check className="mr-2 h-4 w-4" />
              {t('audienceActions.requestSent')}
            </>
          ) : (
            <>
              <Vote className="mr-2 h-4 w-4" />
              {t('audienceActions.requestCoverage')}
            </>
          )}
        </Button>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-4" data-testid="filter-empty-state-suggestions">
          <h3 className="font-display text-2xl text-foreground" style={{ letterSpacing: '-0.02em' }}>
            {t('audienceActions.othersLike')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {suggestions.map((audience) => (
              <CinematicAudienceCard
                key={audience.id}
                audience={audience}
                provider={null}
                formatRelativeTime={formatRelativeTime}
                onClick={() => onAudienceClick(audience.id)}
                onBookmarkToggle={onBookmarkToggle}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
